"use client";

import { FeaturedMedia } from "../FeaturedMedia/FeaturedMedia";
import { MuxPlayer } from "../MuxPlayer/MuxPlayer";
import { type MuxCSSProperties } from "@mux/mux-player-react";
import classNames from "classnames/bind";
import useEmblaCarousel from "embla-carousel-react";
import { WheelGesturesPlugin } from "embla-carousel-wheel-gestures";
import {
  type ComponentRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import styles from "./VideoPlaylist.module.css";

const cx = classNames.bind(styles);

const MUX_PLAYER_STYLE = {
  "--pip-button": "none",
  "--cast-button": "none",
  "--playback-rate-button": "none",
  "--seek-backward-button": "none",
  "--seek-forward-button": "none",
} satisfies MuxCSSProperties;

export type VideoPlaylistItem = {
  title: string;
  playbackId: string;
  thumbnailTimestamp?: number | null;
  thumbnailSrc?: string;
};

export type VideoPlaylistProps = {
  videos: VideoPlaylistItem[];
  className?: string;
};

const EDGE_FADE_RANGE = 0.33;
const PLAYLIST_THUMB_WIDTH = 480;
const PLAYER_POSTER_WIDTH = 1280;

const getMuxThumbnailTime = (time?: number | null) =>
  typeof time === "number" && Number.isFinite(time) && time >= 0
    ? time
    : undefined;

const getMuxThumbnailUrl = (
  playbackId: string,
  { width, time }: { width: number; time?: number } = {
    width: PLAYLIST_THUMB_WIDTH,
  },
) => {
  const params = new URLSearchParams({
    width: String(width),
  });

  if (time != null) {
    params.set("time", String(time));
  }

  return `https://image.mux.com/${encodeURIComponent(playbackId)}/thumbnail.webp?${params}`;
};

export const VideoPlaylist = ({ videos, className }: VideoPlaylistProps) => {
  const [firstVideo] = videos;

  if (!firstVideo) {
    return null;
  }

  if (videos.length === 1) {
    return (
      <FeaturedMedia
        className={className}
        mediaType="video"
        videoPlaybackId={firstVideo.playbackId}
        videoTitle={firstVideo.title}
        videoThumbnailTimestamp={firstVideo.thumbnailTimestamp}
      />
    );
  }

  return <VideoPlaylistCarousel videos={videos} className={className} />;
};

const VideoPlaylistCarousel = ({ videos, className }: VideoPlaylistProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [autoPlay, setAutoPlay] = useState(false);
  const playerRef = useRef<ComponentRef<typeof MuxPlayer>>(null);
  const playlistShellRef = useRef<HTMLDivElement>(null);
  const skipScrollOnMount = useRef(true);

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      align: "start",
      loop: false,
      skipSnaps: false,
      slidesToScroll: 1,
      containScroll: "trimSnaps",
      watchDrag: true,
      dragFree: true,
      axis: "x",
      breakpoints: {
        "(min-width: 1024px)": { axis: "y" },
      },
    },
    [WheelGesturesPlugin()],
  );

  const currentVideo = videos[currentIndex];

  const playVideoAtIndex = useCallback(
    (index: number) => {
      const video = videos[index];
      const player = playerRef.current;

      if (!video) {
        return;
      }

      setCurrentIndex(index);
      setAutoPlay(true);

      if (!player) {
        return;
      }

      if (index === currentIndex) {
        player.currentTime = 0;
      } else {
        const thumbnailTime = getMuxThumbnailTime(video.thumbnailTimestamp);
        player.playbackId = video.playbackId;
        player.poster = getMuxThumbnailUrl(video.playbackId, {
          width: PLAYER_POSTER_WIDTH,
          time: thumbnailTime,
        });
        if (thumbnailTime != null) {
          player.thumbnailTime = thumbnailTime;
        }
      }

      void player.play();
    },
    [currentIndex, videos],
  );

  const handleEnded = useCallback(() => {
    if (currentIndex >= videos.length - 1) {
      setAutoPlay(false);
      return;
    }

    playVideoAtIndex(currentIndex + 1);
  }, [currentIndex, playVideoAtIndex, videos.length]);

  useEffect(() => {
    if (!emblaApi) {
      return;
    }

    const updateEdgeFade = () => {
      const shell = playlistShellRef.current;

      if (!shell) {
        return;
      }

      const { axis, limit, location } = emblaApi.internalEngine();

      if (limit.length <= 0) {
        shell.style.setProperty("--edge-fade-opacity", "0");
        return;
      }

      const slides = emblaApi.slideNodes();
      const lastSlide = slides[slides.length - 1];
      const lastSlideRect = lastSlide?.getBoundingClientRect();
      const isVertical = axis.scroll === "y";
      const cardSize = isVertical
        ? (lastSlideRect?.height ?? 0)
        : (lastSlideRect?.width ?? 0);
      const remaining = Math.max(0, location.get() - limit.min);
      const fadeDistance = cardSize * EDGE_FADE_RANGE;
      const opacity =
        fadeDistance <= 0 ? 0 : Math.min(1, remaining / fadeDistance);

      shell.style.setProperty("--edge-fade-opacity", String(opacity));
    };

    updateEdgeFade();
    emblaApi.on("scroll", updateEdgeFade);
    emblaApi.on("reInit", updateEdgeFade);

    return () => {
      emblaApi.off("scroll", updateEdgeFade);
      emblaApi.off("reInit", updateEdgeFade);
    };
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) {
      return;
    }

    if (skipScrollOnMount.current) {
      skipScrollOnMount.current = false;
      return;
    }

    emblaApi.scrollTo(currentIndex);
  }, [currentIndex, emblaApi]);

  if (!videos.length || !currentVideo) {
    return null;
  }

  return (
    <div
      className={cx("video-playlist", className)}
      aria-label="Video playlist"
    >
      <div className={cx("layout")}>
        <div className={cx("player-frame")}>
          <MuxPlayer
            ref={playerRef}
            className={cx("player")}
            playbackId={currentVideo.playbackId}
            poster={getMuxThumbnailUrl(currentVideo.playbackId, {
              width: PLAYER_POSTER_WIDTH,
              time: getMuxThumbnailTime(currentVideo.thumbnailTimestamp),
            })}
            thumbnailTime={getMuxThumbnailTime(currentVideo.thumbnailTimestamp)}
            autoPlay={autoPlay}
            playsInline
            accentColor="#1d54f5"
            metadata={{
              videoTitle: currentVideo.title,
            }}
            style={MUX_PLAYER_STYLE}
            onEnded={handleEnded}
          />
        </div>
        <div className={cx("playlist-shell")} ref={playlistShellRef}>
          <div className={cx("embla")}>
            <div className={cx("embla-viewport")} ref={emblaRef}>
              <ul className={cx("embla-container")} role="list">
                {videos.map((video, index) => {
                  const isCurrent = index === currentIndex;
                  const thumbnailSrc =
                    video.thumbnailSrc ||
                    getMuxThumbnailUrl(video.playbackId, {
                      width: PLAYLIST_THUMB_WIDTH,
                      time: getMuxThumbnailTime(video.thumbnailTimestamp),
                    });

                  return (
                    <li
                      key={`${video.playbackId}-${index}`}
                      className={cx("embla-slide")}
                    >
                      <button
                        type="button"
                        className={cx("item", { "is-current": isCurrent })}
                        aria-current={isCurrent ? "true" : undefined}
                        onClick={() => playVideoAtIndex(index)}
                      >
                        <span className={cx("thumb")}>
                          <img
                            className={cx("thumb-image")}
                            src={thumbnailSrc}
                            alt=""
                            draggable={false}
                          />
                        </span>
                        <span className={cx("item-title")}>{video.title}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
